
import React, { memo, useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import PasswordProtection from '@/components/settings/PasswordProtection';

interface LockScreenProps {
  showLockScreen: boolean;
  onAuthenticated: () => void;
}

const LockScreen: React.FC<LockScreenProps> = ({ showLockScreen, onAuthenticated }) => {
  // State to control whether to show the pass key form
  const [showPassKeyForm, setShowPassKeyForm] = useState(false);
  // State to force re-render of the password form to clear any saved values
  const [formKey, setFormKey] = useState(Date.now());

  // Reset form state when lock screen visibility changes
  useEffect(() => {
    if (!showLockScreen) {
      setShowPassKeyForm(false);
    } else {
      // When showing the lock screen, generate a new key to force re-render
      setFormKey(Date.now());
      
      // Make sure any form autofill is cleared by setting a timeout
      const timer = setTimeout(() => {
        // Trigger another re-render after a short delay to clear any autofilled fields
        setFormKey(prevKey => prevKey + 1);
      }, 50);
      
      // Set another delayed re-render to ensure field is cleared
      const secondTimer = setTimeout(() => {
        setFormKey(prevKey => prevKey + 1);
      }, 300);
      
      return () => {
        clearTimeout(timer);
        clearTimeout(secondTimer);
      };
    }
  }, [showLockScreen]);

  const handleAuthSuccess = () => {
    // Create a small delay to allow state updates to complete
    setTimeout(() => {
      onAuthenticated();
    }, 100);
  };

  // Handler to toggle pass key form visibility - using useCallback to ensure stability
  const handleShowPassKeyForm = useCallback(() => {
    console.log("LockScreen: Showing pass key form");
    setShowPassKeyForm(true);
  }, []);

  // Handler when going back from passkey to main form - using useCallback to ensure stability
  const handleBackToMain = useCallback(() => {
    console.log("LockScreen: Going back to main form");
    setShowPassKeyForm(false);
    // Force re-render to clear form
    setFormKey(Date.now());
  }, []);

  if (!showLockScreen) {
    return null;
  }
  
  return (
    <>
      <PasswordProtection 
        key={formKey} // Use a unique key to force component recreation when the lock screen shows
        onAuthenticated={handleAuthSuccess}
        showPassKeyForm={showPassKeyForm}
        onShowPassKeyForm={handleShowPassKeyForm}
        onBackFromPassKey={handleBackToMain}
      />
      <div 
        className="fixed inset-0 bg-black/50 cyber-backdrop backdrop-blur-lg z-30" 
        aria-hidden="true"
      />
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(LockScreen);
